import React, { useCallback, useMemo, useState } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import Node, { InputType } from 'components_new/molecules/Node'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FunctionSelect from 'components_new/molecules/Node/FunctionSelect'
import NameField from 'components_new/molecules/Node/NameField'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import DataType from 'types/transformation/enums/DataType'
import {
  DateParserNode,
  Node as TNode
} from 'types/transformation/types/nodes'
import { DATA_TYPES } from 'helpers/Constants'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'

const TYPE_OPTIONS = [
  { id: DataType.DATE, name: DATA_TYPES[DataType.DATE] },
  { id: DataType.TIMESTAMP, name: DATA_TYPES[DataType.TIMESTAMP] }
]

const DATE_TOKENS = [
  { token: 'yyyy', label: 'år (4)' },
  { token: 'yy', label: 'år (2)' },
  { token: 'MM', label: 'månad' },
  { token: 'dd', label: 'dag' },
  { token: '%', label: 'jokertecken' }
]

const TIMESTAMP_TOKENS = [
  ...DATE_TOKENS.filter((entry) => entry.token !== '%'),
  { token: 'HH', label: 'timme' },
  { token: 'mm', label: 'minut' },
  { token: 'ss', label: 'sekund' },
  { token: '%', label: 'jokertecken' }
]

const SEPARATORS_RE = /[-/.:\s,_]/g

/**
 * Lenient, case-insensitive check mirroring the design reference: strip the
 * valid tokens (longest first) for the chosen target type, wildcards and
 * separators — anything left over is an unknown format character. Production
 * should prefer the engine's real token grammar.
 */
const validateFormat = (
  format: string,
  target: DataType
): 'valid' | 'invalid' | null => {
  const trimmed = format.trim()

  if (!trimmed) {
    return null
  }

  const tokens =
    target === DataType.TIMESTAMP
      ? ['yyyy', 'yy', 'MM', 'dd', 'HH', 'mm', 'ss']
      : ['yyyy', 'yy', 'MM', 'dd']

  let rest = trimmed.replace(/%/g, '')

  for (const token of [...tokens].sort((a, b) => b.length - a.length)) {
    rest = rest.replace(new RegExp(token, 'gi'), '')
  }

  rest = rest.replace(SEPARATORS_RE, '')

  return rest.length === 0 ? 'valid' : 'invalid'
}

interface DateParserNodeProps {
  id: string
  data: DateParserNode
  selected: boolean
}

const DateParser = (props: DateParserNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const [expanded, setExpanded] = useState(false)
  const [formatFocused, setFormatFocused] = useState(false)

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  const formatStatus = useMemo(
    () => validateFormat(data.format, data.new_column.type),
    [data.format, data.new_column.type]
  )

  const tokenGroups = [
    { label: 'DATUM', tokens: DATE_TOKENS },
    { label: 'TIDSSTÄMPEL', tokens: TIMESTAMP_TOKENS }
  ]

  // input schema has changed, validate references
  usePruneRefs<DateParserNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, inputSchema)
    }),
    [inputSchema]
  )

  const handleSelectColumn = useCallback(
    (columnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, left_column: columnId } }
            : node
        )
      )
    },
    [setNodes, id]
  )

  const handleChangeFormat = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const format = event.target.value

      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, format } } : node
        )
      )
    },
    [setNodes, id]
  )

  const handleChangeNewColumnName = useCallback(
    (name: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const newNodeData = cloneDeep(node.data) as DateParserNode

          newNodeData.new_column.name = name

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id]
  )

  const handleChangeOutputType = useCallback(
    (outputType: DataType) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const newNodeData = cloneDeep(node.data) as DateParserNode

          newNodeData.new_column.type = outputType

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="EventAvailableOutlined"
      inputType={InputType.SINGLE}
      title={'Date parser'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.DATE_PARSER}
    >
      <Box sx={{ width: '260px' }}>
        <Caption>Källattribut</Caption>
        <FieldSelect
          value={data.left_column}
          options={fieldOptions}
          onChange={handleSelectColumn}
          placeholder="Välj attribut"
        />

        <Box sx={{ mt: 1.25 }}>
          <Caption>Format</Caption>
        </Box>
        <input
          className="nodrag ignore-locked-mode"
          onBlur={() => setFormatFocused(false)}
          onChange={handleChangeFormat}
          onFocus={() => setFormatFocused(true)}
          placeholder="t.ex. yyyy-mm-dd%"
          value={data.format}
          style={{
            background: formatFocused ? '#ffffff' : BOARD.rowBackground,
            border: `1px solid ${
              formatFocused ? BOARD.signalGreen : 'transparent'
            }`,
            borderRadius: '10px',
            boxSizing: 'border-box',
            color: BOARD.textPrimary,
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: '13px',
            outline: 'none',
            padding: '9px 11px',
            width: '100%'
          }}
        />
        {formatStatus ? (
          <Box
            sx={{
              alignItems: 'center',
              color:
                formatStatus === 'valid' ? BOARD.signalGreenDark : '#B91C1C',
              display: 'flex',
              fontFamily: BOARD.fontBody,
              fontSize: '11.5px',
              gap: 0.75,
              mt: 0.5,
              p: '2px'
            }}
          >
            <Icon
              name={formatStatus === 'valid' ? 'Check' : 'WarningAmberOutlined'}
              sx={{ fontSize: '14px' }}
            />
            {formatStatus === 'valid' ? 'Giltigt format' : 'Okänt formattecken'}
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
          <Icon
            name="ArrowDownward"
            sx={{ color: BOARD.textFaint, fontSize: '18px' }}
          />
        </Box>

        <Caption>Nytt kolumnnamn</Caption>
        <NameField
          value={data.new_column.name}
          onChange={handleChangeNewColumnName}
          placeholder="Ny kolumn…"
        />

        <Box sx={{ mt: 1.25 }}>
          <Caption>Måltyp</Caption>
        </Box>
        <FunctionSelect
          value={data.new_column.type}
          options={TYPE_OPTIONS}
          onChange={(value) => handleChangeOutputType(value as DataType)}
          sx={{ width: '100%' }}
        />

        <Box
          role="button"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            alignItems: 'center',
            borderRadius: '9px',
            color: BOARD.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            gap: 0.5,
            mt: 1,
            p: '6px 2px',
            '&:hover': { color: BOARD.textPrimary }
          }}
        >
          <Icon name="Info" sx={{ fontSize: '15px' }} />
          <Box
            sx={{
              flex: 1,
              fontFamily: BOARD.fontDisplay,
              fontSize: '12px',
              fontWeight: 600
            }}
          >
            Format
          </Box>
          <Icon
            name={expanded ? 'ExpandLess' : 'ExpandMore'}
            sx={{ fontSize: '16px' }}
          />
        </Box>

        {expanded ? (
          <Box
            sx={{
              backgroundColor: BOARD.rowBackground,
              borderRadius: '9px',
              p: '8px 10px'
            }}
          >
            {tokenGroups.map((group) => (
              <Box
                key={group.label}
                sx={{ '&:not(:first-of-type)': { mt: 1 } }}
              >
                <Box
                  sx={{
                    color: BOARD.textFaint,
                    fontFamily: BOARD.fontDisplay,
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    mb: 0.5
                  }}
                >
                  {group.label}
                </Box>
                {group.tokens.map((entry) => (
                  <Box
                    key={`${group.label}-${entry.token}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: '1px'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: BOARD.textPrimary,
                        fontFamily: 'ui-monospace, Menlo, monospace',
                        fontSize: '11.5px',
                        fontWeight: 700
                      }}
                    >
                      {entry.token}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: BOARD.textFaint,
                        fontFamily: BOARD.fontBody,
                        fontSize: '11.5px'
                      }}
                    >
                      {entry.label}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    </Node>
  )
}

export default DateParser
